import React, { FC, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { CardType, IPaymentErrors, IPaymentState } from 'features/payment/types';

import { TextInput, TextInputMask } from 'components/ucl/text-input';
import { t } from 'state/intl/translations';
import { PaymentFieldVariations } from 'state/launchdarkly/variations';
import { creditCardNumberMask, expiryMask } from 'utils/masks';

import {
  CardNumberWrapper,
  StyledCvvWrapper,
  StyledExpiryCVVWrapper,
  StyledExpiryWrapper,
} from './styled';

interface IFirstDataCreditCardFormInputs {
  onChange: (name: string, value: string) => void;
  errors: IPaymentErrors;
  paymentValues: IPaymentState;
  supportedCardTypes?: CardType[];
  paymentFieldVariations: PaymentFieldVariations;
  validateCreditCardForm?: () => boolean;
  didBlur: Record<string, boolean>;
  validateOnBlur: (fieldName: string) => void;
}

const CreditCardIcon: FC<React.PropsWithChildren<{ cardType: string | null }>> = ({ cardType }) =>
  (cardType &&
    {
      AMEX: <Icon variant="amex" />,
      DISCOVER: <Icon variant="discover" />,
      DINERS_CLUB: <Icon variant="diners" />,
      JCB: <Icon variant="jcb" />,
      MASTERCARD: <Icon variant="mastercard" />,
      VISA: <Icon variant="visa" />,
    }[cardType]) ||
  null;

const FirstDataCreditCardInputs: FC<React.PropsWithChildren<IFirstDataCreditCardFormInputs>> = ({
  errors,
  onChange,
  paymentValues,
  paymentFieldVariations,
  validateOnBlur,
  didBlur,
}) => {
  const [isCardNumberInputFocused, setIsCardNumberInputFocused] = useState(false);
  const [isExpirationDateFocused, setIsExpirationDateFocused] = useState(false);

  return (
    <>
      {paymentFieldVariations.cardNumber && (
        <CardNumberWrapper>
          <TextInputMask
            aria-label={
              !didBlur.cardNumber && !errors.didAttemptSubmit
                ? t('creditCardNumber')
                : t('pleaseProvideAValid16Digit')
            }
            onChangeText={value => onChange('cardNumber', value)}
            label={t('creditCardNumber')}
            value={paymentValues.cardNumber}
            required
            testID="cardNumber"
            onFocus={() => setIsCardNumberInputFocused(true)}
            onBlur={() => {
              validateOnBlur('cardNumber');
              setIsCardNumberInputFocused(false);
            }}
            errorMessage={
              !didBlur.cardNumber && !errors.didAttemptSubmit
                ? ''
                : errors.cardType || errors.cardNumber
            }
            autoComplete="cc-number"
            keyboardType="numeric"
            mask={creditCardNumberMask}
            placeholder={isCardNumberInputFocused ? '' : '____ ____ ____ ____'}
            endIcon={
              paymentValues.isCardNumberValid ? (
                <CreditCardIcon cardType={paymentValues.cardType} />
              ) : null
            }
          />
        </CardNumberWrapper>
      )}
      <StyledExpiryCVVWrapper>
        {paymentFieldVariations.expiration && (
          <StyledExpiryWrapper>
            <TextInputMask
              autoComplete="cc-exp"
              accessibilityLabel={
                !didBlur.expiry && !errors.didAttemptSubmit
                  ? ''
                  : t('pleaseProvideTheExpirationDate')
              }
              onFocus={() => setIsExpirationDateFocused(true)}
              onBlur={() => {
                validateOnBlur('expiry');
                setIsExpirationDateFocused(false);
              }}
              errorMessage={!didBlur.expiry && !errors.didAttemptSubmit ? '' : errors.expiry}
              keyboardType="numeric"
              label={t('cardExpiration')}
              mask={expiryMask}
              onChangeText={(value: string) => {
                onChange('expiry', value);
              }}
              required
              placeholder={isExpirationDateFocused ? '' : '__/__'}
              testID="expiry"
              value={paymentValues.expiry || ''}
            />
          </StyledExpiryWrapper>
        )}
        {paymentFieldVariations.cvv && (
          <StyledCvvWrapper>
            <TextInput
              accessibilityLabel={
                !didBlur.cvv && !errors.didAttemptSubmit ? '' : t('pleaseProvideTheCreditCardCvv')
              }
              onChangeText={value => onChange('cvv', value)}
              label={t('cvv')}
              value={paymentValues.cvv || ''}
              required
              testID="cvv"
              onBlur={() => validateOnBlur('cvv')}
              errorMessage={!didBlur.cvv && !errors.didAttemptSubmit ? '' : errors.cvv}
              autoComplete="cc-csc"
              keyboardType="numeric"
              // TODO - RN input should support pattern
              // pattern="[0-9]*"
              maxLength={4}
            />
          </StyledCvvWrapper>
        )}
      </StyledExpiryCVVWrapper>
    </>
  );
};

export default FirstDataCreditCardInputs;
